<script lang="ts">
    import tabIcon from "@/assets/gear.svg";

    export default {
        name: "BesokstypkodList",
        tabName: "Besökstypkoder",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs } from "vue";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KButton from "@/components/Button.vue";
    import KTable from "@/components/KTable/KTable.vue";
    import { Column, KTableAction } from "@/components/KTable/types";
    import { Besokstypkod } from "@/tabs/Oppenvard/types";
    import BesokstypkodForm from "@/tabs/Admin/Oppenvard/BesokstypkodForm.vue";
    import { useStore } from "@/store";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const columns: Column[] = [
        {
            header: "Kod",
            field: "code",
            sortField: "code",
            sortable: true,
        },
        {
            header: "Namn",
            field: "displayName",
            sortField: "displayName",
            sortable: true,
        },
        {
            header: "Beskrivning",
            field: "description",
            sortField: "description",
            sortable: true,
            truncate: true,
            tooltipContent: ({ data: description }) => String(description ?? ""),
        },
        {
            header: "Giltig från",
            field: "valid_from_date",
            sortField: "valid_from_date",
            sortable: true,
        },
        {
            header: "Giltig till",
            field: "valid_to_date",
            sortField: "valid_to_date",
            sortable: true,
        },
        {
            header: "Ordningsnummer",
            field: "ordning",
            sortField: "ordning",
            sortable: true,
        },
        {
            header: "Aktiv",
            field: "aktiv",
            sortField: "is_active",
            sortable: true,
        },
    ];

    const dropdownActions: KTableAction[] = [
        {
            title: "Redigera besökstypkod",
            show: () => true,
            callback: ({ data }) => {
                const besokstypkod = data as Besokstypkod;

                store.dispatch("tabs/openTab", {
                    component: BesokstypkodForm,
                    parameters: {
                        besokstypkodId: besokstypkod.id,
                    },
                });
            },
        },
    ];

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);

    const filterParams = ref<{ [key: string]: string | number | boolean } | undefined>();
    const subsetName = ref<string>("default");

    const isLoading = ref(false);

    const besokstypkoder = computed<Besokstypkod[]>(() => {
        return store.getters["besokstypkod/besokstypkodSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        }).map((btk: Besokstypkod) => {
            return {
                ...btk,
                aktiv: btk.is_active ? "Ja" : "Nej",
            };
        });
    });

    const onPage = async ({ page, rows }: { page: number; rows: number }) => {
        listLimit.value = rows;

        await fetchBesokstypkoder({ page, limit: rows });
    };

    const onSort = async ({ sortField, sortOrder }: { sortField: string; sortOrder: number }) => {
        const modifiedFilterParams = {
            ...(filterParams.value ?? {}),
        };

        modifiedFilterParams.ordering = sortOrder === -1 ? `-${sortField}` : sortField;

        filterParams.value = modifiedFilterParams;

        await fetchBesokstypkoder({ page: 1 });
    };

    const fetchBesokstypkoder = async ({ page, limit = listLimit.value }: { page: number; limit?: number }) => {
        isLoading.value = true;

        try {
            const { total, subsetName: fetchedSubsetName } = await store.dispatch("besokstypkod/fetchBesokstypkoder", {
                page,
                limit,
                filterParams: filterParams.value,
            });

            listPage.value = page;
            subsetName.value = fetchedSubsetName;

            if (total) {
                listCount.value = total;
            }
        } catch (error) {
            console.error(error);

            openDialog("Kunde inte hämta besökstypkoder", "error");
        }

        isLoading.value = false;
    };

    const openCreateBesokstypkod = () => {
        store.dispatch("tabs/openTab", {
            component: BesokstypkodForm,
        });
    };

    onBeforeMount(async () => {
        await fetchBesokstypkoder({ page: listPage.value });

        await store.dispatch("besokstypkod/addSubscriber");
    });

    onBeforeUnmount(async () => {
        await store.dispatch("besokstypkod/removeSubscriber");
    });
</script>

<template>
    <div class="besokstypkod-list">
        <admin-lock-border />
        <div class="header">
            <h2>Besökstypkoder</h2>
            <KButton type="button" variant="hollow" size="slim" @click="openCreateBesokstypkod">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Lägg till besökstypkod</span>
            </KButton>
        </div>
        <KTable
            reorderableColumns
            paginator
            alwaysShowPaginator
            loadingMessage="Hämtar besökstypkoder"
            paginatorPosition="bottom"
            name="besokstypkod-table"
            :columns="columns"
            :actions="dropdownActions"
            :value="besokstypkoder"
            :totalRecords="listCount"
            :loading="isLoading"
            @page="onPage"
            @sort="onSort"
        />
    </div>
</template>

<style lang="scss">
    .besokstypkod-list {
        .header {
            h2 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
            margin-bottom: 20px;
        }
    }
</style>
